<template>
  <div class="admin-login">
    <div class="login-container">
      <div class="login-header">
        <div class="logo">
          <h1>🏢 {{ t('adminLogin.title') }}</h1>
          <p>{{ t('adminLogin.subtitle') }}</p>
        </div>
      </div>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="username">{{ t('adminLogin.usernameLabel') }}</label>
          <input
            id="username"
            v-model="credentials.username"
            type="text"
            required
            :disabled="loading"
            :placeholder="t('adminLogin.usernamePlaceholder')"
            autocomplete="username"
          />
        </div>

        <div class="form-group">
          <label for="password">{{ t('adminLogin.passwordLabel') }}</label>
          <input
            id="password"
            v-model="credentials.password"
            type="password"
            required
            :disabled="loading"
            :placeholder="t('adminLogin.passwordPlaceholder')"
            autocomplete="current-password"
          />
        </div>

        <button type="submit" class="login-btn" :disabled="loading">
          <span v-if="loading">🔄 {{ t('adminLogin.authenticating') }}</span>
          <span v-else>🔐 {{ t('adminLogin.accessAdminPortal')}}</span>
        </button>
      </form>

      <div v-if="error" class="error-message">
        <span class="error-icon">⚠️</span>
        {{ error }}
      </div>

      <div class="login-footer">
        <div class="login-switcher">
          <p class="switcher-text">Tenant user?</p>
          <router-link to="/" class="switcher-link">
            <span class="switcher-icon">🤖</span>
            Access AI Scheduler System
          </router-link>
        </div>
        
        <div class="security-notice">
          <p>🔒 {{ t('adminLogin.securityNotice1') }}</p>
          <p>📍 {{ t('adminLogin.securityNotice2') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAdminStore } from '../../stores/admin.js'
import { useAuthStore } from '../../stores/auth.js'

const { t } = useI18n()
const router = useRouter()
const adminStore = useAdminStore()

const credentials = ref({
  username: '',
  password: ''
})

const loading = ref(false)
const error = ref('')

async function handleLogin() {
  if (loading.value) return
  
  loading.value = true
  error.value = ''
  
  try {
    // Clear any existing tenant authentication to prevent confusion
    // (Admin users should not have tenant tokens)  
    const authStore = useAuthStore()
    authStore.clearTokens()
    
    // Use the admin store login method which handles the API call and state management
    await adminStore.login({
      username: credentials.value.username,
      password: credentials.value.password
    })
    
    // Redirect to admin dashboard
    router.push('/saas/dashboard')
    
  } catch (err) {
    console.error('Admin login failed:', err)
    error.value = err.message || 'Login failed. Please check your credentials.'
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  // Router guard handles authentication redirect, no need to check here
})
</script>

<style scoped>
.admin-login {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.login-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  max-width: 400px;
  width: 100%;
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo h1 {
  margin: 0 0 0.5rem 0;
  color: #2d3748;
  font-size: 2rem;
  font-weight: bold;
}

.logo p {
  margin: 0;
  color: #718096;
  font-size: 1rem;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
}

.form-group input {
  padding: 0.875rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: #f7fafc;
}

.form-group input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  background: white;
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-btn {
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.error-message {
  margin-top: 1rem;
  padding: 1rem;
  background: #fed7d7;
  color: #c53030;
  border-radius: 8px;
  border-left: 4px solid #e53e3e;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.error-icon {
  font-size: 1.2rem;
}

.login-footer {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.login-switcher {
  text-align: center;
  margin-bottom: 1.5rem;
}

.switcher-text {
  margin: 0 0 0.75rem 0;
  color: #718096;
  font-size: 0.875rem;
}

.switcher-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #667eea;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.switcher-link:hover {
  background: rgba(102, 126, 234, 0.1);
  transform: translateY(-1px);
}

.switcher-icon {
  font-size: 1rem;
}

.security-notice {
  text-align: center;
}

.security-notice p {
  margin: 0.25rem 0;
  color: #718096;
  font-size: 0.8rem;
}

/* Responsive Design */
@media (max-width: 480px) {
  .admin-login {
    padding: 1rem;
  }
  
  .login-container {
    padding: 2rem;
  }
  
  .logo h1 {
    font-size: 1.5rem;
  }
}
</style>